// ══════════════════════════════════════════════════════════════════════════════
//  Hooks del sistema: useOptimistic, useResolveFlash y el canon de modales (pila ESC, Enter = accion primaria, z automatico). Salio de UI.js (1,666 l) el 2026-09-06 sin cambiar la logica.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Z } from '../../design.js';


// useOptimistic — refleja el cambio al instante; revierte si el servidor falla.
//   const [val, run, pending] = useOptimistic(serverVal)
//   run(nuevoVal, async () => sb.from(...).update(...))   // optimista + commit
function useOptimistic(serverValue) {
  const [override, setOverride] = useState(undefined);
  useEffect(() => { if (override !== undefined && serverValue === override) setOverride(undefined); }, [serverValue, override]);
  const run = async (optimisticValue, commit) => {
    setOverride(optimisticValue);
    try { const r = await commit?.(); if (r && r.error) throw new Error(r.error); return r; }
    catch (e) { setOverride(undefined); throw e; }
  };
  return [override !== undefined ? override : serverValue, run, override !== undefined];
}

// useResolveFlash — devuelve la clase "gb-resolve" durante 800ms cuando `active`
// pasa de false→true (p.ej. un cobro que se marca pagado). Úsalo en el className
// de la fila/celda: <tr className={useResolveFlash(cobro.pagado)} ...>
function useResolveFlash(active) {
  const [on, setOn] = useState(false);
  const prev = useRef(active);
  useEffect(() => {
    if (active && !prev.current) {
      setOn(true);
      const t = setTimeout(() => setOn(false), 800);
      prev.current = active;
      return () => clearTimeout(t);
    }
    prev.current = active;
  }, [active]);
  return on ? "gb-resolve" : "";
}

// ── Stack global de modales para soporte de tecla ESC ──
// Cada modal se registra al montar y se quita al desmontar.
// Solo el modal en la cima (último abierto) responde a ESC, así modales
// anidados se cierran de arriba hacia abajo (uno por pulsación).
const _modalEscStack = [];
let _modalEscBound = false;
const _bindModalEscOnce = () => {
  if (_modalEscBound || typeof document === 'undefined') return;
  _modalEscBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || _modalEscStack.length === 0) return;
    const top = _modalEscStack[_modalEscStack.length - 1];
    if (top) { e.stopPropagation(); top(); }
  });
};
const useEscToClose = (onClose) => {
  useEffect(() => {
    if (typeof onClose !== 'function') return undefined;
    _bindModalEscOnce();
    _modalEscStack.push(onClose);
    return () => {
      const idx = _modalEscStack.lastIndexOf(onClose);
      if (idx >= 0) _modalEscStack.splice(idx, 1);
    };
  }, [onClose]);
};

// ── Stack global de modales para Enter = acción primaria (opt-in) ──
// Espejo del stack de ESC: TODO modal se registra (aunque no declare
// acción) y solo el de la cima responde — un Enter jamás dispara la
// acción de un modal tapado por otro. Se ignora cuando el foco vive en
// un control que ya usa Enter (botón/textarea/select/contentEditable)
// o si un handler local hizo preventDefault. La entrada del stack es
// estable (resuelve el handler desde un ref al disparar): re-renders
// no reordenan la pila.
const _modalEnterStack = [];
let _modalEnterBound = false;
const _bindModalEnterOnce = () => {
  if (_modalEnterBound || typeof document === 'undefined') return;
  _modalEnterBound = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || _modalEnterStack.length === 0) return;
    if (e.repeat || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
    const t = e.target;
    const tag = t?.tagName;
    if (tag === 'BUTTON' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
    const fn = _modalEnterStack[_modalEnterStack.length - 1]?.();
    if (typeof fn !== 'function') return;
    e.preventDefault();
    e.stopPropagation();
    fn();
  });
};
const useEnterToAccept = (onAccept) => {
  const ref = useRef(onAccept);
  ref.current = onAccept;
  useEffect(() => {
    _bindModalEnterOnce();
    const entry = () => (typeof ref.current === 'function' ? ref.current : null);
    _modalEnterStack.push(entry);
    return () => {
      const idx = _modalEnterStack.lastIndexOf(entry);
      if (idx >= 0) _modalEnterStack.splice(idx, 1);
    };
  }, []);
};

// ── Z-index automático de modales ──
// Cada modal sin `zIndex` explícito toma Z.modal + profundidad*10, así los
// anidados se apilan solos en vez de chocar en 1000. Si se pasa `zIndex`, gana.
const _modalZStack = [];
const useModalZ = (explicitZ) => {
  // push/pop en el EFFECT (no en render): puro y robusto al doble-invoke de
  // StrictMode (cada setup crea su propio token). z arranca en Z.modal para que
  // un modal único no parpadee; los anidados se reubican un frame después.
  const [z, setZ] = useState(explicitZ != null ? explicitZ : Z.modal);
  useEffect(() => {
    // La pila registra QUE hay un modal abierto, aunque traiga su z a mano: el
    // que pasa zIndex explícito sigue tapando la pantalla igual, y `hayModalAbierto`
    // lo necesita para vetar el gesto global del buscador. Lo que cambia con el z
    // explícito es solo de dónde sale el número, no si cuenta como abierto.
    const token = {};
    const depth = _modalZStack.length;
    _modalZStack.push(token);
    setZ(explicitZ != null ? explicitZ : Z.modal + depth * 10);
    return () => {
      const i = _modalZStack.indexOf(token);
      if (i >= 0) _modalZStack.splice(i, 1);
    };
  }, [explicitZ]);
  return z;
};


// ── Medir una caja ANTES de pintar ──
// Lo que necesita el tamaño real de su caja para dibujarse (el diagrama del
// flujo, a 1:1 con su contenedor) lo mide en useLayoutEffect: React vuelve a
// dibujar en el mismo cuadro y el primer pintado ya sale en su lugar. Medir
// en useEffect (después de pintar) dibujaba con un tamaño provisional y lo
// corregía un instante después: un salto visible en cada cambio de módulo
// (2026-09-14). Después del primer cuadro, un ResizeObserver sigue los
// cambios. La caja se mide sin su relleno ni su borde (getBoundingClientRect
// los incluye): úsese en cajas sin relleno.
// `clave`: cambia cuando el elemento del ref se desmonta y se vuelve a montar
// (una página que se va y regresa): el efecto se rehace y mide el nodo nuevo.
// Sin ella, el ResizeObserver del nodo viejo avisaba 0 al desmontarse y nadie
// volvía a medir (2026-09-14).
const useMedidaCaja = (ref, clave = null) => {
  const [caja, setCaja] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const poner = (w, h) => setCaja(c => (Math.abs(c.w - w) < 1 && Math.abs(c.h - h) < 1 ? c : { w, h }));
    const r = el.getBoundingClientRect();
    poner(r.width, r.height);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => poner(e.contentRect.width, e.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, clave]);
  return caja;
};

// ¿Hay una ventana de trabajo abierta? La pila de z-index ya lo sabe; lo que
// faltaba era poder preguntárselo desde fuera de un modal. Lo necesita el gesto
// global del buscador: el panel se dibuja por encima y taparía la ventana
// entera, y en el teléfono no hay tecla para cerrarlo.
const hayModalAbierto = () => _modalZStack.length > 0;

export { useEnterToAccept, useEscToClose, useMedidaCaja, useModalZ, useOptimistic, useResolveFlash, hayModalAbierto };
